import { useEffect, useState } from "react";
import { UserPlus, Trash2, Pencil, Shield, User as UserIcon, Building2 } from "lucide-react";
import { listUsers, createUser, updateUser, deleteUser } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const emptyForm = { email: "", password: "", name: "", role: "employee", org_id: "" };

export default function Users() {
  const { user: currentUser, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try {
      setUsers(await listUsers());
    } catch {
      toast.error("Erro ao carregar usuários");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    // Super-admin só cria super-admins
    setForm(isSuperAdmin ? { ...emptyForm, role: "super_admin" } : emptyForm);
    setDialogOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, password: "", name: u.name, role: u.role, org_id: u.org_id || "" });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!editing && (!form.email || !form.password || !form.name)) {
      toast.error("Preencha todos os campos");
      return;
    }
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role };
        if (form.password) payload.password = form.password;
        await updateUser(editing.id, payload);
        toast.success("Usuário atualizado");
      } else {
        const payload = {
          email: form.email,
          password: form.password,
          name: form.name,
          role: form.role,
        };
        await createUser(payload);
        toast.success("Usuário criado");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await deleteUser(confirmDelete.id);
      toast.success("Usuário excluído");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao excluir");
    }
  };

  const roleLabel = (r) =>
    r === "super_admin" ? "Super-admin" : r === "admin" ? "Administrador" : "Funcionário";

  const roleBadgeClass = (r) => {
    if (r === "super_admin") return "bg-amber-500 text-white hover:bg-amber-500 border-0";
    if (r === "admin") return "bg-[#0F2942] text-white hover:bg-[#0F2942] border-0";
    return "bg-slate-100 text-slate-700 hover:bg-slate-100 border-0";
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-[#0F172A]">
            {isSuperAdmin ? "Super-admins" : "Usuários"}
          </h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1">
            {isSuperAdmin
              ? "Gerencie os super-administradores da plataforma."
              : "Gerencie administradores e funcionários do escritório."}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="add-user-button" className="bg-[#0F2942] hover:bg-[#173E63] text-white">
              <UserPlus className="h-4 w-4 mr-2" />
              {isSuperAdmin ? "Novo Super-admin" : "Novo Usuário"}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editing ? "Editar Usuário" : isSuperAdmin ? "Novo Super-admin" : "Novo Usuário"}</DialogTitle>
              <DialogDescription>
                {isSuperAdmin
                  ? "Super-admins têm acesso apenas à gestão de escritórios e outros super-admins."
                  : "Defina o perfil de acesso."}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Nome *</Label>
                <Input data-testid="user-name-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1" />
              </div>
              <div>
                <Label>E-mail {editing ? "(não editável)" : "*"}</Label>
                <Input type="email" data-testid="user-email-input" value={form.email} disabled={!!editing}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1" />
              </div>
              <div>
                <Label>Senha {editing ? "(deixe vazio para manter)" : "*"}</Label>
                <Input type="password" data-testid="user-password-input" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder={editing ? "Nova senha (opcional)" : "Mínimo 6 caracteres"}
                  className="mt-1" />
              </div>
              {!isSuperAdmin && (
                <div>
                  <Label>Perfil</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role-select" className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Administrador</SelectItem>
                      <SelectItem value="employee">Funcionário</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                <Button type="submit" data-testid="user-submit-button" className="bg-[#0F2942] hover:bg-[#173E63] text-white">
                  {editing ? "Salvar" : "Criar"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400 text-sm">Carregando...</div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="users-list">
            {users.map((u) => (
              <li key={u.id} className="p-4 flex items-center gap-3 hover:bg-slate-50 transition-colors">
                <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  u.role === "super_admin" ? "bg-amber-500 text-white"
                  : u.role === "admin" ? "bg-[#0F2942] text-white" : "bg-slate-100 text-slate-600"
                }`}>
                  {u.role === "super_admin" ? <Shield className="h-5 w-5" />
                   : u.role === "admin" ? <Shield className="h-5 w-5" /> : <UserIcon className="h-5 w-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#0F172A]">{u.name}</p>
                  <p className="text-xs text-slate-500 truncate">{u.email}</p>
                </div>
                <Badge className={roleBadgeClass(u.role)}>{roleLabel(u.role)}</Badge>
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  {u.id !== currentUser?.id && (
                    <Button size="sm" variant="outline" onClick={() => setConfirmDelete(u)}
                      data-testid={`delete-user-${u.id}`}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário?</AlertDialogTitle>
            <AlertDialogDescription>
              Excluir <b>{confirmDelete?.name}</b>? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="confirm-delete-user" className="bg-red-600 hover:bg-red-700">
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
